import React, { useContext, useEffect, useState } from 'react';

import _ from 'lodash';

import { ImagePreview } from './ImagePreview';
import CountryFlag from '../common/CountryFlag';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { IconButton } from '../common/IconButton';
import MultiCountryEditor from '../common/editors/MultiCountryEditor';
import TextSearchInput from '../common/TextSearchInput';
import { SwitchInput } from '../common/SwitchInput';
import ModalMoveImageClusterImages from './ModalMoveImageClusterImages';
import ModalLinkedClusters from './ModalLinkedClusters';
import ImagePCAScatterPlot from '../embedding-explorer/ImagePCAScatterPlot';
import GridLayoutHeader from '../common/layout/GridLayoutHeader';
import BadgeColorHash from '../common/BadgeColorHash';


function vehicleToString(vehicle) {
  return _.values(_.omit(vehicle, 'trimId')).map(v => _.isObject(v) ? _.values(v).join(' ') : v).join(' ');
}

export default function ImageClusterGallery({ clusterId, filterByYear, preloadedCluster }) {
  const { page } = useContext(LegoAdminPageContext);
  const clusterSvc = page.service('services/data/image-clusters');
  const embeddingSvc = page.service('services/data/fusebox-model-embeddings');
  const [cluster, setCluster] = useState(preloadedCluster || null);
  const [ignoreFilter, setIgnoreFilter] = useState(false);
  const [countriesFilter, setCountriesFilter] = useState(null);
  const [textFilter, setTextFilter] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // PCA scatter of the cluster's image embeddings (loaded on demand via the graph button).
  const [showGraph, setShowGraph] = useState(false);
  const [graphItems, setGraphItems] = useState(null); // null = not loaded yet
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState(null);

  useEffect(() => {
    !preloadedCluster && page.runAsync(clusterSvc.get(clusterId).then(cluster => setCluster(cluster || false)));
  }, [clusterId]);

  // A different cluster means a different embedding set — drop what we loaded for the previous one.
  useEffect(() => {
    setShowGraph(false);
    setGraphItems(null);
    setGraphError(null);
  }, [clusterId]);

  const toggleSelected = (imgId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(imgId)) next.delete(imgId); else next.add(imgId);
      return next;
    });
  };

  const loadGraph = () => {
    setGraphLoading(true);
    setGraphError(null);
    page.runAsync(
      embeddingSvc.find({ query: { clusterId } })
        .then((res) => {
          const items = (res?.items || [])
            .filter(it => it.imgUrl && Array.isArray(it.embedding) && it.embedding.length)
            .map(it => ({ imgId: String(it.imgId), imgUrl: it.imgUrl, vector: it.embedding, clusterId: it.clusterId }));
          setGraphItems(items);
          setGraphLoading(false);
        })
        .catch((err) => {
          setGraphError(String(err?.message || err));
          setGraphLoading(false);
        }),
    );
  };

  const toggleGraph = () => {
    const next = !showGraph;
    setShowGraph(next);
    if (next && graphItems == null && !graphLoading) loadGraph();
  };

  const refreshCluster = () => {
    setSelectedIds(new Set());
    page.runAsync(clusterSvc.get(clusterId).then(cluster => setCluster(cluster || false)));
    // Images may have left this cluster — reload the PCA embeddings too if they were already loaded.
    if (graphItems != null) loadGraph();
  };

  const openLinkedClusters = () => {
    page.openModal(<ModalLinkedClusters clusterId={clusterId} />,
      { title: <>Clusters linked to <BadgeColorHash id={clusterId} /></>, fullScreenFixed: true });
  };

  const openMoveModal = () => {
    page.openModal(<ModalMoveImageClusterImages
      sourceCluster={cluster}
      selectedImageIds={[...selectedIds]}
      onSave={() => { page.closeModal(); refreshCluster(); }}
      onCancel={() => page.closeModal()} />,
      { title: 'Move images to another cluster', fullScreen: true, noPadding: true });
  };

  if (cluster) {
    let { images } = cluster;
    if (_.isNumber(filterByYear) && !ignoreFilter) {
      images = _.filter(images, i => i.vehicle?.year === filterByYear);
    }
    // const filteredCount = images.length;

    if (countriesFilter?.length) {
      images = _.filter(images, i => _.includes(countriesFilter, i.vehicle?.countryCode));
    }

    if (textFilter) {
      images = _.filter(images, i => _.includes(JSON.stringify(i.vehicle).toLowerCase(), textFilter.toLowerCase()) || (textFilter.length > 3 && i.id.toString().includes(textFilter)));      
    }

    if(!selectMode && selectedIds.size > 0) {
      images = _.filter(images, i => selectedIds.has(String(i.id)));
    }

    images = _.orderBy(images, 'vehicle.year');

    return <div style={{ overflowY: 'auto', position: 'relative', height: '100%', width: '100%' }}>      
      <GridLayoutHeader >
        <div className={'bg-light-info d-flex flex-wrap align-items-center px-3 p-1 position-sticky top-0'} style={{gap: '5px'}}>
          <span>{images.length} images {images.length !== cluster.images.length ? `(of ${cluster.images.length})` : null}</span>

          {filterByYear ?
            <IconButton icon={ignoreFilter ? 'filter_alt' : 'clear'} level={ignoreFilter ? 'primary' : 'danger'}
              onClick={() => setIgnoreFilter(!ignoreFilter)}>
              {ignoreFilter ? `Click to filter by year ${filterByYear}` : `Filtering ${filterByYear}, see all ${cluster.images.length}`}
            </IconButton> : null}

          <span style={{ minWidth: '140px' }}>
            <MultiCountryEditor value={countriesFilter} onChange={setCountriesFilter} placeholder={'Select countries'} />
          </span>

          <span style={{ minWidth: '150px' }}>
            <TextSearchInput value={textFilter} onChange={setTextFilter} placeholder={'Filter trims...'} />
          </span>

          <span>
            <SwitchInput value={selectMode} onChange={setSelectMode}>
              Select mode
            </SwitchInput>
          </span>

          <span>
            <IconButton icon={'scatter_plot'} level={showGraph ? 'primary' : 'secondary'} onClick={toggleGraph}>
              {showGraph ? 'Hide PCA graph' : 'PCA graph'}
            </IconButton>
          </span>

          <span>
            <IconButton icon={'hub'} level={'secondary'} title={'Clusters linked/rejected to this one (image_cluster_links)'}
              onClick={openLinkedClusters}>
              Linked clusters
            </IconButton>
          </span>

          {selectMode && selectedIds.size > 0 ? <span className={'ml-3'}>
            <span className={'mr-2 badge badge-primary'}>{selectedIds.size} selected</span>
            <IconButton icon={'drive_file_move'} level={'danger'} onClick={openMoveModal}>
              Move to other cluster
            </IconButton>
          </span> : null}
        </div>

        <div className={`align-middle h-100 w-100 overflow-hidden`} style={{ display: 'grid', gridTemplateColumns: showGraph ? '1fr 2fr' : '1fr', gap: '10px' }}>
          {showGraph ? <div className={'bg-light h-100 p-1 overflow-hidden'}>
            {graphLoading ? <div className={'text-secondary p-3'}>Loading embeddings for PCA…</div> : null}

            {graphError ? <div className={'text-danger p-2'}>Failed to load embeddings: {graphError}</div> : null}

            {!graphLoading && !graphError && graphItems && !graphItems.length
              ? <div className={'text-secondary p-3'}>No embeddings available for this cluster's images.</div> : null}

            {!graphLoading && !graphError && graphItems && graphItems.length ? <>
              <ImagePCAScatterPlot
                items={graphItems}
                selectMode={true}
                selectedKeys={selectedIds}
                title={''}
                onSelectionChange={setSelectedIds}
              />
            </> : null}
          </div> : null}

          <span style={{ minHeight: '72px' }} className={`h-100 w-100 overflow-auto align-items-top p-2`}>
            {_.map(images, ({ id, url, vehicle }, j) => {
              const vehicleString = vehicleToString(vehicle);
              const newYear = vehicle.year !== images[j - 1]?.vehicle?.year;
              const imgKey = String(id);
              const isSelected = selectMode && selectedIds.has(imgKey);
              const onImageClick = selectMode ? () => toggleSelected(imgKey) : undefined;

              return <span style={newYear ? { borderLeft: 'solid 3px', paddingLeft: '4px' } : {}} className={`inline-block`} key={id + '-' + j} title={vehicleString}>
                  {newYear ? <div key={'border-left border-primary pl-2 year' + j}>
                    <h3 onClick={() => setFilterByYear(vehicle.year)} className={'text-primary'}>{vehicle.year}</h3>
                  </div> : null}

                  <span className={`inline-block p-1 border ${isSelected ? 'border-primary bg-light-primary' : ''}`}
                    style={selectMode ? { borderStyle: isSelected ? 'dashed' : 'solid' } : { borderColor: 'transparent' }}>
                    <ImagePreview img200 cover key={id} url={url || ''} className={'bg-light'} zoom context={vehicle} selected={isSelected} />

                    <div className={'small zoom-90'} onClick={selectMode ? onImageClick : undefined}>
                      {selectMode ? <input type='checkbox' className={'mr-1 align-middle'} checked={isSelected} onChange={() => toggleSelected(imgKey)} onClick={(e) => e.stopPropagation()} /> : null}
                      <CountryFlag countryCode={(vehicle.countryCode || '').toLowerCase()} />
                      <span className={'ml-1'}>{vehicle.year}</span>
                    </div>
                  </span>
                </span>;
            })}
          </span>
        </div>
      </GridLayoutHeader></div>;
  } else if (cluster === false) {
    return <span className={'align-items-top text-danger'}>_id "<code>{clusterId}</code>" NOT FOUND</span>;
  } else {
    return <div className={`border bg-light rounded p-5`}>
      <div style={{ minHeight: '72px' }} className={'inline-block text-center text-secondary'}>
        Loading cluster<br />
        <span className={'small'}>{clusterId}</span>...
      </div>
    </div>;
  }
}
